import { useTranslation } from "react-i18next"

import type { FilePickerSource } from "../types"

import { Upload } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { Spinner } from "@/components/ui/spinner"

interface FilePickerFooterProps {
  tab: FilePickerSource
  localFilesCount: number
  selectedFilesCount: number
  isUploading?: boolean
  onCancel: () => void
  onConfirm: () => void
}

export function FilePickerFooter({
  tab,
  localFilesCount,
  selectedFilesCount,
  isUploading = false,
  onCancel,
  onConfirm,
}: FilePickerFooterProps) {
  const { t } = useTranslation()
  const isDisabled =
    isUploading || (tab === "local" ? localFilesCount === 0 : selectedFilesCount === 0)

  const getStatusText = () => {
    if (isUploading) {
      const count = tab === "local" ? localFilesCount : selectedFilesCount
      return t("filepicker.uploading_files", {
        defaultValue: "Processing {{count}} file(s)...",
        count,
      })
    }

    if (tab === "local") {
      return localFilesCount === 0
        ? t("filepicker.no_files_selected", { defaultValue: "No files selected" })
        : t("filepicker.files_ready_to_upload", {
            defaultValue: "{{count}} file(s) ready to upload",
            count: localFilesCount,
          })
    }

    return selectedFilesCount === 0
      ? t("filepicker.no_files_selected", { defaultValue: "No files selected" })
      : t("filepicker.files_selected", {
          defaultValue: "{{count}} file(s) selected",
          count: selectedFilesCount,
        })
  }

  const getButtonText = () => {
    if (isUploading) {
      return t("filepicker.processing", { defaultValue: "Processing..." })
    }
    return tab === "local"
      ? t("filepicker.upload_files", { defaultValue: "Upload Files" })
      : t("filepicker.add_selected", { defaultValue: "Add Selected" })
  }

  return (
    <div className="bg-background flex-shrink-0 border-t px-3 py-3 sm:px-6 sm:py-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="text-muted-foreground text-xs sm:text-sm">{getStatusText()}</div>
        <div className="flex items-center gap-2 sm:gap-3">
          <Button className="flex-1 sm:flex-none" variant="outline" onClick={onCancel}>
            {t("filepicker.cancel", { defaultValue: "Cancel" })}
          </Button>
          <Button
            className="min-w-[100px] flex-1 sm:min-w-[120px] sm:flex-none"
            disabled={isDisabled}
            onClick={onConfirm}
          >
            {isUploading ? (
              <Spinner className="mr-1 h-4 w-4 sm:mr-2" />
            ) : (
              <Upload className="mr-1 h-4 w-4 sm:mr-2" />
            )}
            {getButtonText()}
          </Button>
        </div>
      </div>
    </div>
  )
}
